import { HttpErrorResponse } from '@angular/common/http';
import { Injectable, OnDestroy } from '@angular/core';
import { Group, GroupAccountMutationData } from '@wb/core/interfaces/group';
import {
  AvailableGroupAccountsGQL,
  GroupAccount,
  GroupAccountsGQL,
  GroupAccountUser,
  GroupGQL,
  GroupResponse,
  GroupsGQL,
  GroupsQueryVariables,
  GroupsResponse,
  GroupUsersGQL,
} from '@wb/core/schema/query-definitions/group.graphql';
import { QueryRef } from 'apollo-angular';
import { BehaviorSubject, Observable, ReplaySubject, Subject } from 'rxjs';
import { map, takeUntil } from 'rxjs/operators';
import {
  CreateGroupGQL,
  CreateGroupInput,
  DeleteGroupGQL,
  GroupUpdateInput,
  UpdateGroupAccountsGQL,
  UpdateGroupGQL,
} from '../schema/mutation-definitions/groups.graphql';

@Injectable({
  providedIn: 'root',
})
export class GroupService implements OnDestroy {
  public groupsLoading$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

  public groupAccountsChanged$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

  public groupGeneralChanged$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

  public addAccountsValid$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

  private groupsQueryRef: QueryRef<GroupsResponse, GroupsQueryVariables> | undefined;

  private destroyed$: Subject<void> = new Subject<void>();

  private groups$: ReplaySubject<Group[]> = new ReplaySubject<Group[]>(1);

  private groupsCount$: ReplaySubject<number> = new ReplaySubject<number>(1);

  private groupAccounts$: BehaviorSubject<GroupAccount[]> = new BehaviorSubject<GroupAccount[]>([]);

  private groupAccountUsers$: BehaviorSubject<GroupAccountUser[]> = new BehaviorSubject<GroupAccountUser[]>([]);

  private selectedGroup$: BehaviorSubject<Group | undefined> = new BehaviorSubject<Group | undefined>(undefined);

  constructor(
    private readonly groupGQL: GroupGQL,
    private readonly groupsGQL: GroupsGQL,
    private readonly groupUsersGQL: GroupUsersGQL,
    private readonly updateGroupGQL: UpdateGroupGQL,
    private readonly createGroupGQL: CreateGroupGQL,
    private readonly deleteGroupGQL: DeleteGroupGQL,
    private readonly groupAccountsGQL: GroupAccountsGQL,
    private readonly updateGroupAccountsGQL: UpdateGroupAccountsGQL,
    private readonly availableGroupAccountsGQL: AvailableGroupAccountsGQL,
  ) {}

  public async filterGroups(filters: GroupsQueryVariables) {
    this.groupsLoading$.next(true);
    this.groupsQueryRef = this.groupsGQL.watch(filters, {
      fetchPolicy: 'network-only',
    });

    this.groupsQueryRef.valueChanges
      .pipe(
        map((result) => result.data),
        takeUntil(this.destroyed$),
      )
      .subscribe({
        next: (response: GroupsResponse) => {
          this.groups$.next(response.groups.success ? response.groups.groups : []);
          this.groupsCount$.next(response.groups.count);
          this.groupsLoading$.next(false);
        },
        error: (err: HttpErrorResponse) => {
          this.groups$.next([]);
          if (err) {
            console.error(err);
          }
        },
      });
  }

  // Query single group and update selectedGroup$
  public loadGroup(groupUid: string): Promise<void> {
    this.groupsLoading$.next(true);
    return new Promise((resolve, reject) => {
      this.groupGQL
        .fetch({ uid: groupUid }, { fetchPolicy: 'network-only' })
        .pipe(
          map((result) => result.data),
          takeUntil(this.destroyed$),
        )
        .subscribe({
          next: async (response: GroupResponse) => {
            if (response.group.success && response?.group?.group) {
              this.selectedGroup$.next(response.group.group);
              this.groupAccounts$.next(await this.getGroupAccounts(groupUid));
              this.groupAccountUsers$.next(await this.getGroupUsers(groupUid));
              this.groupsLoading$.next(false);
              resolve();
            }
            reject();
          },
          error: (err: HttpErrorResponse) => {
            if (err) {
              console.error(err);
            }
            reject();
          },
        });
    });
  }

  public createGroup(input: CreateGroupInput): Promise<void> {
    this.groupsLoading$.next(true);
    return new Promise((resolve, reject) => {
      this.createGroupGQL
        .mutate({ input })
        .pipe(takeUntil(this.destroyed$))
        .subscribe({
          next: () => {
            this.groupsLoading$.next(false);
            resolve();
          },
          error: (err: HttpErrorResponse) => {
            if (err) {
              console.error(err);
            }
            reject();
          },
        });
    });
  }

  public deleteGroup(groupUid: string): Promise<void> {
    this.groupsLoading$.next(true);
    return new Promise((resolve, reject) => {
      this.deleteGroupGQL
        .mutate({ uid: groupUid })
        .pipe(takeUntil(this.destroyed$))
        .subscribe({
          next: () => {
            this.groupsLoading$.next(false);
            resolve();
          },
          error: (err: HttpErrorResponse) => {
            if (err) {
              console.error(err);
            }
            reject();
          },
        });
    });
  }

  public updateGroup(groupUid: string, input: GroupUpdateInput): Promise<void> {
    this.groupsLoading$.next(true);
    return new Promise((resolve, reject) => {
      this.updateGroupGQL
        .mutate({ uid: groupUid, input })
        .pipe(takeUntil(this.destroyed$))
        .subscribe({
          next: () => {
            this.groupsLoading$.next(false);
            resolve();
          },
          error: (err: HttpErrorResponse) => {
            if (err) {
              console.error(err);
            }
            reject();
          },
        });
    });
  }

  public updateGroupAccounts(groupUid: string, modifiedAccountsList: GroupAccountMutationData[]): Promise<void> {
    return new Promise((resolve, reject) => {
      this.updateGroupAccountsGQL
        .mutate({
          uid: groupUid,
          input: modifiedAccountsList,
        })
        .subscribe({
          next: () => {
            this.groupsLoading$.next(false);
            resolve();
          },
          error: (err: HttpErrorResponse) => {
            if (err) {
              console.error(err);
            }
            reject();
          },
        });
    });
  }

  private getGroupAccounts(groupUid: string): Promise<GroupAccount[]> {
    this.groupsLoading$.next(true);
    return new Promise((resolve, reject) => {
      this.groupAccountsGQL
        .fetch({ uid: groupUid }, { fetchPolicy: 'network-only' })
        .pipe(
          map((result) => result.data.groupAccounts.accounts),
          takeUntil(this.destroyed$),
        )
        .subscribe({
          next: (groupAccounts: GroupAccount[]) => {
            this.groupsLoading$.next(false);
            resolve(groupAccounts);
          },
          error: (err: HttpErrorResponse) => {
            if (err) {
              console.error(err);
            }
            reject();
          },
        });
    });
  }

  private getGroupUsers(groupUid: string): Promise<GroupAccountUser[]> {
    this.groupsLoading$.next(true);
    return new Promise((resolve, reject) => {
      this.groupUsersGQL
        .fetch({ uid: groupUid }, { fetchPolicy: 'network-only' })
        .pipe(
          map((result) => result.data.groupUsers.groupAccountUsers),
          takeUntil(this.destroyed$),
        )
        .subscribe({
          next: (groupAccountUsers: GroupAccountUser[]) => {
            if (groupAccountUsers) {
              this.groupsLoading$.next(false);
              resolve(groupAccountUsers);
            }
            reject();
          },
          error: (err: HttpErrorResponse) => {
            if (err) {
              console.error(err);
            }
            reject();
          },
        });
    });
  }

  // Used for group assignments. Accounts should not be able to be assigned to multiple groups.
  public async getAvailableGroupAccounts(): Promise<GroupAccount[]> {
    this.groupsLoading$.next(true);
    return new Promise((resolve, reject) => {
      this.availableGroupAccountsGQL
        .fetch({}, { fetchPolicy: 'network-only' })
        .pipe(
          map((result) => result.data.availableGroupAccounts.groupAccounts),
          takeUntil(this.destroyed$),
        )
        .subscribe({
          next: (result: GroupAccount[]) => {
            this.groupsLoading$.next(false);
            resolve(result);
          },
          error: (err) => {
            if (err) {
              console.error(err);
            }
            reject([]);
          },
        });
    });
  }

  get groups(): Observable<Group[]> {
    return this.groups$.asObservable();
  }

  get groupsCount(): Observable<number> {
    return this.groupsCount$.asObservable();
  }

  get selectedGroup(): Observable<Group | undefined> {
    return this.selectedGroup$.asObservable();
  }

  get groupAccounts(): Observable<GroupAccount[]> {
    return this.groupAccounts$.asObservable();
  }

  get groupAccountUsers(): Observable<GroupAccountUser[]> {
    return this.groupAccountUsers$.asObservable();
  }

  set groupAccountsChanged(valid: boolean) {
    this.groupAccountsChanged$.next(valid);
  }

  set groupGeneralChanged(valid: boolean) {
    this.groupGeneralChanged$.next(valid);
  }

  set addAccountsValid(valid: boolean) {
    this.addAccountsValid$.next(valid);
  }

  ngOnDestroy() {
    this.destroyed$.next();
    this.destroyed$.complete();
  }
}
